import { useCallback, useEffect, useRef, useState } from 'react';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';

import { DateTimeFormat } from '@xemplo/date-time';

import { getDisplayValue } from '../date-field.helper';
import { DateFieldRhfProps, DateValue } from '../date-field.types';

export function useDateFieldRhf(
  field: ControllerRenderProps<FieldValues, string>,
  props: DateFieldRhfProps,
) {
  const isFirstRender = useRef(true);
  const isClearingField = useRef(false);

  const { onBlur, onChange, onValueChange, dateFormat = DateTimeFormat.Default } = props;

  const [displayValue, setDisplayValue] = useState(
    getDisplayValue(field.value, dateFormat),
  );

  const { onBlur: fieldOnBlur, onChange: fieldOnChange } = field;

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      fieldOnBlur(); // for RHF validation
      onBlur?.(e);
    },
    [fieldOnBlur, onBlur],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      isFirstRender.current = false;
      setDisplayValue(e.target.value);
      onChange?.(e);
    },
    [onChange],
  );

  const handleValueChange = useCallback(
    (value?: DateValue) => {
      fieldOnChange(value);
      onValueChange?.(value);
    },
    [fieldOnChange, onValueChange],
  );

  useEffect(() => {
    // User is in the process of updating date and currently entered value is invalid date
    if (field.value === null) return;
    if (!field.value) {
      const wasOurClear = isClearingField.current;
      isClearingField.current = false;
      if (!wasOurClear) setDisplayValue('');
      return;
    }
    isClearingField.current = false;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const nextDisplayValue = getDisplayValue(field.value, dateFormat);
      setDisplayValue(nextDisplayValue ?? '');
    }
  }, [field.value, dateFormat]);

  return {
    displayValue,
    handleBlur,
    handleChange,
    handleValueChange,
  };
}
